import { NATAL_GROUPS, type NatalSection } from '@/api/groups-natal'
import { BirthRequired } from '@/app/components/BirthRequired/BirthRequired'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { Motion } from '@/app/components/Motion/Motion'
import { NatalChart, type NatalChartAspect, type NatalChartPlanet } from '@/app/components/NatalChart/NatalChart'
import { Planet } from '@/app/components/Planet/Planet'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate, useNavigateSearchParams } from '@/app/hooks/useNavigate'
import ArrowSvg from '@/app/icons/arrow-right.svg?react'
import RadioActiveSvg from '@/app/icons/radio-active.svg?react'
import RadioSvg from '@/app/icons/radio.svg?react'
import { useNatal } from '@/app/state/natal'
import { useSession } from '@/app/state/session'
import { ASPECT_NAMES, getNatalAspect, getNatalAspectQuestion, PLANET_NAMES } from '@/app/utils/natal'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

function isNatalChartPlanet(name: string): name is NatalChartPlanet {
  return name in PLANET_NAMES
}

function getAspectId(aspect: { planet1: string; planet2: string; aspect: number }) {
  return `${aspect.planet1}-${aspect.planet2}-${aspect.aspect}`
}

const NATAL_TABS = [
  ...NATAL_GROUPS,
  { id: 'aspects', title: 'Аспекты' },
]

const DEFAULT_SECTION_ID = NATAL_GROUPS[0]?.sections[0]?.id ?? null

export const NatalChartPage: FunctionalComponent = () => {
  const route = useNavigate()
  const [params, navigate] = useNavigateSearchParams()

  const fetch = useNatal(s => s.fetch)
  const chart = useNatal(useShallow(s => s.chart))
  const hasExactBirthDetails = useSession(s => !!s.profile?.birthTime && !!s.profile?.location)

  const back = useBack('~/tools')
  const swiperRef = useRef<SwiperType | null>(null)
  const tabParam = params.get('tab')
  const activeIdxFromParams = Math.max(
    0,
    NATAL_TABS.findIndex(item => item.id === tabParam),
  )
  const [activeIdx, setActiveIdx] = useState(activeIdxFromParams)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(DEFAULT_SECTION_ID)
  const [activeAspectId, setActiveAspectId] = useState<string | null>(null)
  const [hasSelectedItem, setHasSelectedItem] = useState(false)
  const [birthDetailsRequiredVisible, setBirthDetailsRequiredVisible] = useState(false)

  const activeSection = NATAL_GROUPS
    .flatMap(group => group.sections)
    .find(section => hasSelectedItem && section.id === activeSectionId)

  const points = activeSection?.points ?? []
  const houses = points.filter(item => item.type === 'house').map(item => item.value)
  const accents = points.filter(item => item.type === 'planet').map(item => item.value)
  const availableAspects = chart?.aspects.flatMap(aspect =>
    isNatalChartPlanet(aspect.planet1) && isNatalChartPlanet(aspect.planet2)
      ? [{
        ...aspect,
        planet1: aspect.planet1,
        planet2: aspect.planet2,
      }]
      : []
  ) ?? []
  const aspects = [
    ...availableAspects.filter(aspect => aspect.aspect !== 0),
    ...availableAspects.filter(aspect => aspect.aspect === 0),
  ]
  const emptyAspectRowsCount = Math.max(0, 7 - aspects.length)
  const activeAspect = aspects.find(aspect => getAspectId(aspect) === activeAspectId)
  const highlightedAspect: NatalChartAspect | undefined = activeAspect
    ? [activeAspect.planet1, activeAspect.planet2, activeAspect.aspect]
    : undefined

  const activeGroup = NATAL_GROUPS.at(activeIdx)

  function getQuestion(question: string, groupId: string) {
    const params = new URLSearchParams({
      q: question,
    })

    if (groupId) {
      params.set('groupId', groupId)
    }

    route(`~/chats/zoe?${params.toString()}`)
  }

  function canUseSection(section: NatalSection) {
    if (section.requiresBirthTimeAndLocation && !hasExactBirthDetails) {
      return false
    }

    return true
  }

  function useSection(section: NatalSection) {
    if (section.requiresBirthTimeAndLocation && !hasExactBirthDetails) {
      setBirthDetailsRequiredVisible(true)
      return false
    }

    return true
  }

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIdxFromParams) {
      swiperRef.current.slideTo(activeIdxFromParams, 0)
    }

    if (activeIdx === activeIdxFromParams) return

    setActiveIdx(activeIdxFromParams)
  }, [activeIdx, activeIdxFromParams])

  function setTabParam(idx: number) {
    const nextTab = idx === 0 ? null : NATAL_TABS[idx]?.id
    const currentTab = tabParam && NATAL_TABS.some(item => item.id === tabParam)
      ? tabParam
      : null

    if (currentTab === nextTab) return

    navigate((prev) => {
      const next = new URLSearchParams(prev)

      if (nextTab) {
        next.set('tab', nextTab)
      }
      else {
        next.delete('tab')
      }

      return next
    }, {
      replace: true,
    })
  }

  return (
    <Layout>
      {birthDetailsRequiredVisible && (
        <BirthRequired
          onClose={() => {
            setBirthDetailsRequiredVisible(false)
          }}
        />
      )}

      <Layout.Content
        fullWidth
        class={styles.desktop}
      >
        {!chart
          ? (
            <Loader.Container>
              <Loader type='natal-chart' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Row gap={20}>
                <Col gap={15}>
                  {NATAL_TABS.map((group, idx) => (
                    <UI.Tab
                      key={group.id}
                      active={activeIdx === idx}
                      onClick={() => {
                        setTabParam(idx)
                        swiperRef.current?.slideTo(idx)
                      }}
                    >
                      {group.title}
                    </UI.Tab>
                  ))}
                </Col>

                <div class={styles.grid}>
                  <Row gap={10} class={styles.list}>
                    {activeIdx === 4 && (
                      aspects.map((aspect) => {
                        const aspectId = getAspectId(aspect)
                        const aspectName = ASPECT_NAMES[aspect.aspect] ?? `${aspect.aspect}°`
                        const question = getNatalAspectQuestion(aspect)

                        return (
                          <UI.Panel
                            hover
                            key={aspectId}
                            class={styles.item}
                            onPointerEnter={() => {
                              setHasSelectedItem(true)
                              setActiveSectionId(null)
                              setActiveAspectId(current => current === aspectId ? null : aspectId)
                            }}
                            onPointerLeave={() => {
                              setHasSelectedItem(true)
                              setActiveSectionId(null)
                              setActiveAspectId(null)
                            }}
                            onClick={() => {
                              getQuestion(question, 'aspects')
                            }}
                          >
                            <Row gap={5}>
                              <Col
                                alignItems='center'
                                justifyContent='space-between'
                              >
                                <Col
                                  gap={2}
                                  alignItems='center'
                                >
                                  <Text
                                    fontSize={18}
                                    fontWeight={600}
                                  >
                                    {aspectName}
                                  </Text>

                                  <ArrowSvg
                                    width={16}
                                    height={16}
                                    strokeWidth={2.3}
                                    class={styles.arrow}
                                  />
                                </Col>

                                <div class={styles.signs}>
                                  <span class={styles.planet}>
                                    <Planet planet={aspect.planet1} />
                                  </span>
                                  <span class={styles.planet}>
                                    <Planet planet={aspect.planet2} />
                                  </span>
                                </div>
                              </Col>

                              <Text
                                fontSize={16}
                                fontSizeMobile={14}
                                opacity={0.5}
                              >
                                {getNatalAspect(aspect)}
                              </Text>
                            </Row>
                          </UI.Panel>
                        )
                      })
                    )}

                    {activeGroup?.sections.map(section => (
                      <UI.Panel
                        hover
                        key={section.id}
                        class={classNames(styles.item, {
                          [styles.disabled]: !canUseSection(section),
                        })}
                        onPointerEnter={() => {
                          if (!canUseSection(section)) return

                          setHasSelectedItem(true)
                          setActiveAspectId(null)
                          setActiveSectionId(section.id)
                        }}
                        onPointerLeave={() => {
                          if (!canUseSection(section)) return

                          setHasSelectedItem(true)
                          setActiveAspectId(null)
                          setActiveSectionId(null)
                        }}
                        onClick={() => {
                          if (!useSection(section)) return

                          getQuestion(section.question, activeGroup.id)
                        }}
                      >
                        <Row gap={5}>
                          <Col
                            alignItems='center'
                            justifyContent='space-between'
                          >
                            <Col
                              gap={2}
                              alignItems='center'
                            >
                              <Text
                                fontSize={18}
                                fontWeight={600}
                              >
                                {section.title}
                              </Text>

                              <ArrowSvg
                                width={16}
                                height={16}
                                strokeWidth={2.3}
                                class={styles.arrow}
                              />
                            </Col>

                            <div class={styles.signs}>
                              {section.points.map((point, idx) => (
                                point.type === 'planet'
                                  ? (
                                    <span key={idx} class={styles.planet}>
                                      <Planet planet={point.value} />
                                    </span>
                                  )
                                  : null
                              ))}
                            </div>
                          </Col>

                          <Text
                            fontSize={16}
                            fontSizeMobile={14}
                            opacity={0.5}
                          >
                            {section.description}
                          </Text>
                        </Row>
                      </UI.Panel>
                    ))}
                  </Row>
                  <div
                    class={styles.chart}
                  >
                    <NatalChart
                      id='desktop'
                      houses={houses}
                      accents={accents}
                      aspect={highlightedAspect}
                      chart={chart}
                      accentScale={1.5}
                      aspectStrokeWidth={2}
                      negativeAspectColor='rgb(219, 37, 55)'
                      positiveAspectColor='rgb(55 165 7)'
                    />
                  </div>
                </div>
              </Row>
            </Motion>
          )}
      </Layout.Content>

      <Layout.Content
        onBack={back}
        class={styles.mobile}
      >
        {!chart
          ? (
            <Loader.Container>
              <Loader type='natal-chart' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header>
                Натальная карта
              </Layout.Header>

              <Row
                gap={15}
              >
                <UI.Panel
                  class={styles.chart}
                  type='transparent'
                >
                  <NatalChart
                    id='mobile'
                    houses={houses}
                    accents={accents}
                    aspect={highlightedAspect}
                    chart={chart}
                    accentScale={1.5}
                    aspectStrokeWidth={2}
                    negativeAspectColor='rgb(219, 37, 55)'
                    positiveAspectColor='rgb(55 165 7)'
                  />
                </UI.Panel>

                <UI.Panel
                  class={styles.panel}
                >
                  <UI.Tabs>
                    {NATAL_TABS.map((group, idx) => (
                      <UI.Tab
                        key={group.id}
                        height={48}
                        active={activeIdx === idx}
                        onClick={() => {
                          setTabParam(idx)
                          swiperRef.current?.slideTo(idx)
                        }}
                      >
                        {group.title}
                      </UI.Tab>
                    ))}
                  </UI.Tabs>

                  <div class={styles.swiperWrapper}>
                    <Swiper
                      initialSlide={activeIdxFromParams}
                      slidesPerView={1}
                      className={styles.swiper}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper

                        if (swiper.activeIndex !== activeIdxFromParams) {
                          swiper.slideTo(activeIdxFromParams, 0)
                        }
                      }}
                      onSlideChange={(swiper) => {
                        setActiveIdx(swiper.activeIndex)
                        setTabParam(swiper.activeIndex)
                      }}
                    >
                      {NATAL_GROUPS.map((group, groupIdx) => (
                        <SwiperSlide key={group.id} class={styles.swiperSlide}>
                          <div class={styles.table}>
                            {group.sections.map(section => {
                              const isActive = activeIdx === groupIdx
                                && activeSectionId === section.id

                              return (
                                <div
                                  key={section.id}
                                  class={classNames(styles.row, {
                                    [styles.active]: isActive,
                                    [styles.disabled]: section.requiresBirthTimeAndLocation
                                      && !hasExactBirthDetails,
                                  })}
                                >
                                  <div class={styles.rowInner}>
                                    <div
                                      class={styles.cell}
                                      onClick={() => {
                                        if (!useSection(section)) return

                                        setHasSelectedItem(true)
                                        setActiveAspectId(null)
                                        setActiveSectionId(current => current === section.id ? null : section.id)
                                      }}
                                    >
                                      <div
                                        class={classNames(styles.radio, {
                                          [styles.active]: isActive,
                                        })}
                                      >
                                        {isActive ? <RadioActiveSvg /> : <RadioSvg />}
                                      </div>

                                      {section.title}
                                    </div>
                                    <div
                                      class={classNames(styles.cell, styles.interact)}
                                      onClick={() => {
                                        if (!useSection(section)) {
                                          return
                                        }

                                        getQuestion(section.question, group.id)
                                      }}
                                    >
                                      <Col alignItems='center'>
                                        {isActive
                                          ? (
                                            <Motion initialX={-1} initialO={0.3}>
                                              <UI.Button
                                                size='xs'
                                                type='primary'
                                              >
                                                Открыть
                                              </UI.Button>
                                            </Motion>
                                          )
                                          : (
                                            <>
                                              {section.points.map((point, idx) => (
                                                point.type === 'planet'
                                                  ? (
                                                    <span key={idx} class={styles.planet}>
                                                      <Planet planet={point.value} />
                                                    </span>
                                                  )
                                                  : null
                                              ))}

                                              <ArrowSvg class={styles.arrow} />
                                            </>
                                          )}
                                      </Col>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </SwiperSlide>
                      ))}
                      <SwiperSlide class={styles.swiperSlide}>
                        <div class={styles.table}>
                          {aspects.map((aspect) => {
                            const aspectId = getAspectId(aspect)
                            const isActive = activeAspectId === aspectId
                            const aspectName = ASPECT_NAMES[aspect.aspect]
                              ?? `${aspect.aspect}°`
                            const question = getNatalAspectQuestion(aspect)

                            return (
                              <div
                                key={aspectId}
                                class={classNames(styles.row, {
                                  [styles.active]: isActive,
                                })}
                              >
                                <div class={styles.rowInner}>
                                  <div
                                    class={styles.cell}
                                    onClick={() => {
                                      setHasSelectedItem(true)
                                      setActiveSectionId(null)
                                      setActiveAspectId(current => current === aspectId ? null : aspectId)
                                    }}
                                  >
                                    <div
                                      class={classNames(styles.radio, {
                                        [styles.active]: isActive,
                                      })}
                                    >
                                      {isActive ? <RadioActiveSvg /> : <RadioSvg />}
                                    </div>
                                    {aspectName}
                                  </div>

                                  <div
                                    class={styles.cell}
                                    onClick={() => {
                                      getQuestion(question, 'aspects')
                                    }}
                                  >
                                    {isActive
                                      ? (
                                        <Motion initialX={-1} initialO={0.3}>
                                          <UI.Button
                                            type='primary'
                                            size='xs'
                                          >
                                            Открыть
                                          </UI.Button>
                                        </Motion>
                                      )
                                      : (
                                        <>
                                          <span class={styles.planet}>
                                            <Planet planet={aspect.planet1} />
                                          </span>
                                          <span class={styles.planet}>
                                            <Planet planet={aspect.planet2} />
                                          </span>
                                          <ArrowSvg class={styles.arrow} />
                                        </>
                                      )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                          {Array.from(
                            { length: emptyAspectRowsCount },
                            (_, idx) => (
                              <div
                                key={`empty-aspect-${idx}`}
                                class={classNames(styles.row, styles.empty)}
                              >
                                <div class={styles.rowInner} />
                              </div>
                            ),
                          )}
                        </div>
                      </SwiperSlide>
                    </Swiper>
                  </div>
                </UI.Panel>
              </Row>
            </Motion>
          )}
      </Layout.Content>
    </Layout>
  )
}
